import { useCallback, useMemo, useRef, useState } from 'react'

import { DefaultMenuItem, MenuItemDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { Plus, Trash } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { createColDefs } from '@fortem-monorepo/react-ag'
import { createContextMenu, defaultGetRowId } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { CustomAgGridProps } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { JSONTable } from '@fortem-monorepo/react-ui'
import { Swapagg } from '@fortem-monorepo/web-sdk'

import {
  useDeleteSwapaggMappingMutation,
  usePushMappingToSwapaggMutation,
  useUpdateSwapaggMappingMutation,
} from '@/react-query/mutations/mappings'

export interface SWAPAGGTableProps extends CustomAgGridProps {}

export const SWAPAGGTable: React.FC<SWAPAGGTableProps> = ({ query, rowData, ...props }) => {
  const { hasPermissions } = useUserContext()
  const [dialogData, setDialogData] = useState<Swapagg.SwapaggMapping>()
  const [isUpdatingRow, setIsUpdatingRow] = useState(false)

  const pushMappingToSwapaggMutation = usePushMappingToSwapaggMutation()
  const deleteSwapaggMappingMutation = useDeleteSwapaggMappingMutation()
  const updateSwpaaggMappingMutation = useUpdateSwapaggMappingMutation()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_MAPPINGS],
  }))

  // For use in the action Dialog Components
  const parsedDialogData = useMemo(() => {
    return {
      ...dialogData,
      in_prod: dialogData?.in_prod === '1' ? true : false,
    }
  }, [dialogData])

  const { Dialog: AddDialog, withConfirmation: confirmAdd } = useConfirmModal({
    title: 'Add to SWAPAGGG',
  })

  const { Dialog: RemoveDialog, withConfirmation: confirmRemove } = useConfirmModal({
    title: 'Remove from SWAPAGG',
    description: 'Do you really want to remove from SWAPAGG? This will also reset the entire row.',
  })

  const getContextMenu = useCallback(
    createContextMenu<Swapagg.SwapaggMapping>((params) => {
      const pushToSWAPAGGAction = {
        name: 'Add to SWAPAGG',
        action: () => {
          setDialogData(params.node?.data)
          confirmAdd(async () => {
            await pushMappingToSwapaggMutation.mutateAsync({
              id: params.node?.data?.id!,
            })
          })
        },
        icon: renderToString(<Plus size={16} />),
      }

      const removeFromSWAPAGGAction = {
        name: 'Remove from SWAPAGGG & Reset',
        action: () => {
          setDialogData(params.node?.data)
          confirmRemove(async () => {
            await deleteSwapaggMappingMutation.mutateAsync({
              id: params.node?.data?.id!,
            })
          })
        },
        icon: renderToString(<Trash size={16} />),
        cssClasses: ['hover:bg-red-100/50!', 'font-medium!'],
      }

      const contextMenuItems: (DefaultMenuItem | MenuItemDef<Swapagg.SwapaggMapping>)[] = [
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'paste',
        'separator',
        'export',
      ]

      if (hasWriteAccess) {
        contextMenuItems.unshift('separator')
        if (params?.node?.data?.banktraderef) {
          contextMenuItems.unshift(removeFromSWAPAGGAction)
        }
        contextMenuItems.unshift(pushToSWAPAGGAction)
      }

      return contextMenuItems
    }),
    [hasPermissions],
  )

  const GridAPI = useRef<AgGridReact>(null)

  const colDefs = createColDefs<Swapagg.SwapaggMapping>([
    { field: 'id', hide: true },
    {
      field: 'in_prod',
      headerName: 'In Swapagg?',
      cellClassRules: {
        ['bg-red-200']: (params) => params?.value === '0',
        ['bg-green-300']: (params) => params?.value === '1',
      },
      cellRenderer: () => <></>,
    },
    { field: 'modified_datetime_utc', headerName: 'Modified', filter: false },
    { field: 'trade_date', headerName: 'Trade Date', filter: false },
    { field: 'fortemidentifier', headerName: 'Fortem Id' },
    { field: 'fxforwardfortemidentifier', headerName: 'FxFwd Id' },
    {
      field: 'banktraderef',
      editable: !isUpdatingRow && hasWriteAccess,
      onCellValueChanged: async (params) => {
        try {
          setIsUpdatingRow(true)
          await updateSwpaaggMappingMutation.mutateAsync({
            rowData: params.node?.data!,
            oldValue: params.oldValue,
            newValue: params.newValue,
            updatedField: params.colDef.field!,
          })
        } finally {
          setIsUpdatingRow(false)
        }
      },
    },
    { field: 'prodccy', filter: false },
    { field: 'fundname', headerName: 'Fund' },
    { field: 'fundccy', filter: false },
    { field: 'counterpartycode', headerName: 'Agent' },
    { field: 'bondisin', headerName: 'Bond Isin' },
    { field: 'underlyingbondisinref' },
    { field: 'assetswapfortemidentifier', headerName: 'Asset Swap Id' },
    { field: 'basisswapfortemidentifier', headerName: 'Basis Swap Id' },
    { field: 'ntbookingsecnum', filter: false },
    { field: 'paydelaybdays', filter: false },
  ])

  const DialogTable = useMemo(
    () => (
      <JSONTable
        data={[parsedDialogData]}
        excludeKeys={[
          'modified_datetime_utc',
          'assetswapfortemidentifier',
          'basisswapfortemidentifier',
          'ntbookingsecnum',
          'paydelaybdays',
          'fxforwardfortemidentifier',
        ]}
      />
    ),
    [parsedDialogData],
  )

  return (
    <>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={query} title="SWAPAGG" />
        <WidgetContent loading={query?.isLoading}>
          <CustomAgGrid
            ref={GridAPI}
            columnDefs={colDefs}
            rowData={rowData}
            getContextMenuItems={getContextMenu}
            updating={isUpdatingRow}
            getRowId={defaultGetRowId}
            {...props}
          />
        </WidgetContent>
      </Widget>
      <AddDialog>{DialogTable}</AddDialog>
      <RemoveDialog>{DialogTable}</RemoveDialog>
    </>
  )
}
